import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { ChevronDown, Cloud, Info, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";

const API =
  (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

/**
 * PropertyRightmoveSection
 *
 * Renders every Rightmove RTDF field that has NEITHER a CMS-field mapping
 * nor a manual global-mapping value, allowing the admin to set a value
 * specific to THIS property. Values are stored as
 * ``propertyForm.cms_overrides[Rightmove_Field_Name]`` and persist to
 * the property document via the standard auto-save.
 *
 * Fields are grouped by their Rightmove catalogue "group" (Address, Details,
 * Amenities, Regulatory, Lettings, Media, ...). Each group is a collapsible
 * native <details> panel to keep the property edit dialog tidy.
 */
export function PropertyRightmoveSection({ overrides, onChange, cmsValues, onCmsChange, onProxyBlur, onProxyFocus, showDbNames, fieldExtras, groupFilter, hideHeader }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grouped, setGrouped] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  // Per-field { info, visible } settings driven by the CMS Field Map.
  // Missing entries default to { info: "", visible: true }.
  const [fieldSettings, setFieldSettings] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [{ data }, settingsResp] = await Promise.all([
          axios.get(`${API}/admin/integrations/rightmove-unmapped-fields`, { withCredentials: true }),
          axios.get(`${API}/admin/cms-field-settings`, { withCredentials: true }).catch(() => ({ data: { settings: {} } })),
        ]);
        if (cancelled) return;
        setFieldSettings(settingsResp?.data?.settings || {});
        // Fold "Location" fields (lat/lng, POV_*) into the "Address" group so
        // the admin sees every geo-related field in one visual block, and put
        // Address first in the list. Also sort Address fields in a natural
        // form-like order so postcodes / town / display_address feel like a
        // real address form rather than a random dump.
        const rawGrouped = data.grouped || {};
        const merged = {};
        const addressFields = [
          ...(rawGrouped["Address"] || []),
          ...(rawGrouped["Location"] || []),
        ];
        if (addressFields.length > 0) {
          const order = [
            "House_Name_Number", "Street_Name", "Address_2", "Address_3", "Address_4",
            "Town", "Postcode", "Postcode_1", "Postcode_2", "Country_Code",
            "Region", "Sub_Region", "Town_City",
            "Display_Address",
            "Latitude", "Longitude",
            "POV_Latitude", "POV_Longitude", "POV_Heading", "POV_Pitch", "POV_Zoom",
          ];
          const idxOf = (name) => {
            const i = order.indexOf(name);
            return i === -1 ? 999 : i;
          };
          addressFields.sort((a, b) => idxOf(a.name) - idxOf(b.name));
          merged["Address"] = addressFields;
        }
        // Preserve every other group in original order.
        Object.entries(rawGrouped).forEach(([g, fields]) => {
          if (g !== "Address" && g !== "Location") merged[g] = fields;
        });
        setGrouped(merged);
        setTotalCount(data.count || 0);
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || e.message || "Load failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setValue = (fieldName, value) => {
    const next = { ...(overrides || {}) };
    if (value === "" || value === null || value === undefined) {
      delete next[fieldName];
    } else {
      next[fieldName] = value;
    }
    onChange(next);
  };

  // For fields with `proxy_cms_field`, read/write goes directly to the
  // property document's CMS field (single source of truth) instead of the
  // cms_overrides bag. Falls back to the override storage if no
  // cmsValues/onCmsChange were supplied.
  const getValueFor = (f) => {
    if (f.proxy_cms_field && cmsValues) {
      const v = cmsValues[f.proxy_cms_field];
      return v === null || v === undefined ? "" : v;
    }
    return overrides?.[f.name] ?? "";
  };
  const setValueFor = (f, value) => {
    if (f.proxy_cms_field && onCmsChange) {
      onCmsChange(f.proxy_cms_field, value);
    } else {
      setValue(f.name, value);
    }
    // When any postcode-carrying address field changes, kick off a debounced
    // postcodes.io lookup to auto-fill Latitude / Longitude. Runs only when
    // we can build a plausible full UK postcode.
    if (f.name === "Postcode" || f.name === "Postcode_1" || f.name === "Postcode_2") {
      const pcFull    = f.name === "Postcode"   ? value : (cmsValues?.postcode || "");
      const pcOutward = f.name === "Postcode_1" ? value : (overrides?.Postcode_1 || "");
      const pcInward  = f.name === "Postcode_2" ? value : (overrides?.Postcode_2 || "");
      const candidate = (pcFull && pcFull.trim())
        || (pcOutward && pcInward && `${pcOutward} ${pcInward}`.trim())
        || "";
      scheduleGeocode(candidate);
    }
  };

  // Debounced postcode-driven geocoder. Fills `latitude` / `longitude`
  // via the parent onCmsChange when postcodes.io returns a hit.
  const geocodeTimer = useRef(null);
  const lastLookedUp = useRef("");
  const scheduleGeocode = (candidate) => {
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    if (!onCmsChange) return;
    const cleaned = (candidate || "").toUpperCase().replace(/\s+/g, " ").trim();
    if (!/^[A-Z]{1,2}\d[A-Z\d]?\s\d[A-Z]{2}$/.test(cleaned)) return;
    if (cleaned === lastLookedUp.current) return;
    geocodeTimer.current = setTimeout(() => {
      lastLookedUp.current = cleaned;
      fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned.replace(/\s+/g, ""))}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.status === 200 && d.result) {
            onCmsChange("latitude", d.result.latitude);
            onCmsChange("longitude", d.result.longitude);
          }
        })
        .catch(() => { /* best-effort */ });
    }, 500);
  };

  const setCount = useMemo(
    () => Object.keys(overrides || {}).filter((k) => overrides[k] !== "").length,
    [overrides]
  );

  if (loading) {
    return (
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Rightmove fields…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-t border-border pt-6" data-testid="rightmove-section-error">
        <div className="text-sm text-red-800 p-3 border border-red-300 bg-red-50 rounded">
          Couldn&apos;t load Rightmove field list: {error}
        </div>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="border-t border-border pt-6" data-testid="rightmove-section-empty">
        <div className="flex items-center gap-2 mb-2">
          <Cloud className="h-4 w-4 text-primary" />
          <h3 className="font-serif text-lg">Rightmove</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Every Rightmove RTDF field is already mapped globally under
          Integrations → Rightmove Mapping. Nothing to override on this property.
        </p>
      </div>
    );
  }

  return (
    <div
      className={hideHeader ? "space-y-4" : "border-t border-border pt-6 space-y-4"}
      data-testid="rightmove-section"
    >
      {!hideHeader && (
        <>
          <div className="flex items-center gap-2">
            <Cloud className="h-4 w-4 text-primary" />
            <h3 className="font-serif text-lg">Rightmove</h3>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="rightmove-section-count">
              {setCount} / {totalCount} set
            </span>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Values you set below are used ONLY when this property is pushed to
            Rightmove — they don&apos;t affect the CMS or any other property. Fields
            already covered by the global mapping are hidden.
          </p>
        </>
      )}

      {Object.entries(grouped).map(([groupName, fields]) => {
        // Hide entire groups (heading + fields) from the admin property edit
        // dialog. Lettings is sales-only; Identity is a Rightmove-internal
        // bookkeeping group with no editorial content.
        if (groupName === "Lettings" || groupName === "Identity") return null;
        // When a specific group is requested (via `groupFilter`) render only
        // that one — used by AdminDashboard to place each Rightmove group at
        // its own configured position in the property edit modal.
        if (groupFilter && groupName !== groupFilter) return null;
        // Respect per-field visibility from the CMS Field Map.
        const visibleFields = fields.filter((f) => {
          const s = fieldSettings[f.settings_key];
          return !s || s.visible !== false;
        });
        if (visibleFields.length === 0) return null;
        const isAddress = groupName === "Address";
        // Address fields render in a 2-col grid so postcodes/town/etc. sit
        // side-by-side like a real address form. Long-form fields (display
        // address, textarea-y ones) still span both columns.
        const isSpanTwo = (f) =>
          f.name === "Display_Address" ||
          /description|summary|details|features/i.test(f.name);
        // Auto-open the group whenever ANY field in it already has a value —
        // either through a proxy CMS field or via an override. This keeps
        // scraped-in data (description, beds, baths…) visible immediately
        // after "Import from URL → Apply All" instead of hiding it behind
        // a collapsed panel.
        const groupHasValue = visibleFields.some((f) => {
          const v = f.proxy_cms_field
            ? cmsValues?.[f.proxy_cms_field]
            : overrides?.[f.name];
          if (v === undefined || v === null || v === "") return false;
          if (Array.isArray(v)) return v.length > 0;
          return true;
        });
        return (
          <details
            key={groupName}
            className="border border-border rounded"
            data-testid={`rightmove-group-${groupName.replace(/\s+/g, "-").toLowerCase()}`}
            open={groupHasValue || undefined}
          >
            <summary className="px-3 py-2 cursor-pointer select-none flex items-center justify-between text-sm font-medium bg-muted/40 hover:bg-muted/60">
              <span>{groupName}</span>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                {visibleFields.length} field{visibleFields.length === 1 ? "" : "s"}
                <ChevronDown className="h-3.5 w-3.5" />
              </span>
            </summary>
            <div
              className={
                isAddress
                  ? "p-3 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3"
                  : "p-3 space-y-3 border-t border-border"
              }
            >
              {visibleFields.map((f) => (
                <div
                  key={f.name}
                  className={isAddress && isSpanTwo(f) ? "sm:col-span-2" : ""}
                >
                  <RightmoveFieldInput
                    field={f}
                    value={getValueFor(f)}
                    onChange={(v) => setValueFor(f, v)}
                    onBlur={f.proxy_cms_field && onProxyBlur ? () => onProxyBlur(f.proxy_cms_field) : undefined}
                    onFocus={f.proxy_cms_field && onProxyFocus ? () => onProxyFocus(f.proxy_cms_field) : undefined}
                    showDbNames={showDbNames}
                    extras={fieldExtras?.[f.name]}
                    info={(fieldSettings[f.settings_key] || {}).info || ""}
                  />
                </div>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
}

function RightmoveFieldInput({ field, value, onChange, onBlur, onFocus, showDbNames, extras, info }) {
  const testid = `rightmove-override-${field.name}`;
  // When ?show=yes is on, print the DB storage key next to the field label.
  // For proxy fields we show the underlying CMS column; otherwise we show
  // the cms_overrides bucket key.
  const dbName = field.proxy_cms_field
    ? field.proxy_cms_field
    : `cms_overrides.${field.name}`;
  // Info popover: rendered when the CMS Field Map has non-blank help text
  // for this field. Click the ⓘ icon to reveal the note in a popover.
  const infoButton = info ? (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-600 hover:text-blue-800 hover:bg-blue-50"
          title="Field guidance — click to view"
          data-testid={`rightmove-info-${field.name}`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="max-w-xs text-xs leading-relaxed"
        data-testid={`rightmove-info-content-${field.name}`}
      >
        {info}
      </PopoverContent>
    </Popover>
  ) : null;
  const label = (
    <Label className="text-sm flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5">
        {field.name.replace(/_/g, " ")}
        {field.required ? <span className="text-red-600">*</span> : null}
        {infoButton}
        {showDbNames && (
          <span
            className="ml-1 inline-block text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 align-middle"
            data-testid={`db-field-badge-${field.name}`}
          >
            {dbName}
          </span>
        )}
      </span>
      {extras ? <span className="ml-auto flex items-center gap-1">{extras}</span> : null}
    </Label>
  );

  const desc = field.description ? (
    <p className="text-xs text-muted-foreground">{field.description}</p>
  ) : null;

  // ---------- Boolean → Checkbox ----------
  if (field.type === "Boolean") {
    const checked = value === true || value === "true" || value === 1 || value === "1";
    return (
      <div className="flex items-start gap-3">
        <Checkbox
          id={testid}
          checked={checked}
          onCheckedChange={(v) => onChange(v ? true : "")}
          data-testid={testid}
        />
        <div className="space-y-0.5">
          <Label htmlFor={testid} className="text-sm cursor-pointer">
            {field.name.replace(/_/g, " ")}
            {field.required ? <span className="ml-1 text-red-600">*</span> : null}
          </Label>
          {desc}
        </div>
      </div>
    );
  }

  // ---------- SingleSelect → Select dropdown ----------
  if (field.type === "SingleSelect" && Array.isArray(field.options) && field.options.length > 0) {
    return (
      <div className="space-y-1">
        {label}
        <Select value={value || "__none__"} onValueChange={(v) => onChange(v === "__none__" ? "" : v)}>
          <SelectTrigger className="rounded-none" data-testid={testid}>
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              <span className="text-muted-foreground">— Not set —</span>
            </SelectItem>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {desc}
      </div>
    );
  }

  // ---------- MultiSelect with fixed options → checkbox list ----------
  if (field.type === "MultiSelect" && Array.isArray(field.options) && field.options.length > 0) {
    // Selected values live as either an array or a comma-separated string —
    // normalise to a lowercase Set for O(1) membership checks.
    const arr = Array.isArray(value)
      ? value
      : (value || "").split(",").map((s) => s.trim()).filter(Boolean);
    const selected = new Set(arr.map((s) => String(s).toLowerCase()));
    const toggle = (opt) => {
      const key = opt.toLowerCase();
      const next = new Set(selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Emit as an array of the ORIGINAL-case option labels the admin picked
      // so the backend can look them up in its label→int map.
      const outArr = field.options.filter((o) => next.has(o.toLowerCase()));
      onChange(outArr.length > 0 ? outArr : "");
    };
    return (
      <div className="space-y-1">
        {label}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 border border-input rounded p-2 bg-background">
          {field.options.map((opt) => {
            const id = `${testid}-${opt.replace(/\s+/g, "-").toLowerCase()}`;
            const isChecked = selected.has(opt.toLowerCase());
            return (
              <label
                key={opt}
                htmlFor={id}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/40 px-1.5 py-1 rounded"
              >
                <Checkbox
                  id={id}
                  checked={isChecked}
                  onCheckedChange={() => toggle(opt)}
                  data-testid={id}
                />
                <span>{opt}</span>
              </label>
            );
          })}
        </div>
        {desc}
      </div>
    );
  }

  // ---------- MultiSelect / Array of strings (no fixed options) → text input ----------
  if (field.type === "MultiSelect" || field.type === "Array of strings") {
    const displayValue = Array.isArray(value) ? value.join(", ") : value || "";
    // Features stores a comma-separated bullet list — render as a 3-row
    // textarea so admins can see and edit multiple features comfortably.
    // Capped at 10 comma-separated values.
    const isFeatures = /^features$/i.test(field.name);
    const InputCmp = isFeatures ? Textarea : Input;
    const inputProps = isFeatures ? { rows: 3 } : {};
    const featureCount = isFeatures
      ? displayValue.split(",").map((s) => s.trim()).filter(Boolean).length
      : 0;
    const handleChange = (e) => {
      let v = e.target.value;
      if (isFeatures) {
        const items = v.split(",").map((s) => s.trim()).filter(Boolean);
        if (items.length > 10) v = items.slice(0, 10).join(", ");
      }
      onChange(v);
    };
    return (
      <div className="space-y-1">
        {label}
        <InputCmp
          value={displayValue}
          onChange={handleChange}
          placeholder={
            Array.isArray(field.options) && field.options.length
              ? `e.g. ${field.options.slice(0, 3).join(", ")}`
              : "Comma-separated values"
          }
          className="rounded-none"
          data-testid={testid}
          {...inputProps}
        />
        {isFeatures && (
          <p
            className={`text-[11px] ${featureCount >= 10 ? "text-amber-600" : "text-muted-foreground"}`}
            data-testid={`${testid}-count`}
          >
            {featureCount} / 10 features
          </p>
        )}
        {desc}
        {Array.isArray(field.options) && field.options.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Allowed: {field.options.slice(0, 8).join(", ")}
            {field.options.length > 8 ? ` (+${field.options.length - 8} more)` : ""}
          </p>
        )}
      </div>
    );
  }

  // ---------- Number / Integer ----------
  if (field.type === "Number" || field.type === "Integer") {
    return (
      <div className="space-y-1">
        {label}
        <Input
          type="number"
          value={value === "" || value === null || value === undefined ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          onBlur={onBlur}
          className="rounded-none"
          placeholder="0"
          data-testid={testid}
        />
        {desc}
      </div>
    );
  }

  // ---------- Date ----------
  if (field.type === "Date") {
    return (
      <div className="space-y-1">
        {label}
        <Input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none"
          data-testid={testid}
        />
        {desc}
      </div>
    );
  }

  // ---------- Timestamp ----------
  if (field.type === "Timestamp") {
    return (
      <div className="space-y-1">
        {label}
        <Input
          type="datetime-local"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-none"
          data-testid={testid}
        />
        {desc}
      </div>
    );
  }

  // ---------- String (default): long text uses Textarea ----------
  const isLong = /description|summary|details|features/i.test(field.name);
  if (isLong) {
    return (
      <div className="space-y-1">
        {label}
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className="rounded-none"
          rows={3}
          data-testid={testid}
        />
        {desc}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {label}
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        className="rounded-none"
        data-testid={testid}
      />
      {desc}
    </div>
  );
}
